import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Box, Divider, Stack, Typography, useMediaQuery } from '@mui/material'
import SliderMui from '@mui/material/Slider'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import GraphPeriod from 'components/GraphPeriod'
import LineGraph, { GraphPeriodType } from 'components/LineGraph/LineGraph'
import { DataPoint } from 'components/LineGraph/types'
import { buildDataHistoricalSpotPrices } from 'components/LineGraph/utils'
import Location from 'components/Location'
import { reviewPath } from 'components/Routes'
import { referralsPath, tradePath } from 'components/Routes/Routes'
import DefaultLayout from 'components/layout/DefaultLayout'
import LoadingWrapper from 'components/layout/LoadingWrapper'
import {
  CURRENT_PURCHASE_FEE,
  TRADING_IS_PAUSED,
  DEFAULT_BUY_AMOUNT
} from 'constants/index'
import { NumberValue } from 'd3'
import { useMutateCreateReviewOrder } from 'hooks/useTrading'
import { useHistoricalSpotPrices } from 'hooks/useInventory'
import useProducts from 'hooks/useProducts'
import useSpotPrice from 'hooks/useSpotPrice'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import theme from 'utils/theme'
import { formatDateToString, formattedPrice } from 'utils/util'
import {
  MAX_QUANTITY_MG,
  MIN_QUANTITY,
  decrementQuantity,
  incrementQuantity,
  stepFor
} from 'utils/quantitySlider'
import PauseTrading from '../PauseTrading'
import InputNumber from './InputNumber'
import useStyles from './styles'
import CountUpPrice from 'components/CountUpPrice'

export default function TradeProduct(): JSX.Element {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { user, setToast } = useAuth()

  const isDesktopView = useMediaQuery(theme.breakpoints.up('md'))

  const { product, products, productId } = useProducts()

  const [graphPeriod, setGraphPeriod] = useState<GraphPeriodType>('1w')
  const [tradingPaused, setTradingPaused] = useState<boolean>(false)

  const [headerHeight, setHeaderHeight] = useState<number>(0)
  const [productBullionHeight, setProductBullionHeight] = useState<number>(0)

  const unitType = product?.unitType ?? 'mg'

  const [weightSlider, setWeightSlider] = useState<number>(0)
  const [amount, setAmount] = useState<string>('0')

  const [error, setError] = useState<
    { message: string; error: boolean } | undefined
  >()
  const [step, setStep] = useState<number>(MIN_QUANTITY)

  // weightSlider holds the quantity in the product's unit_type (e.g. mg
  // for gold/silver). All price math is mg-native: amount = quantity * pricePerMg.
  const calculateAmount = useCallback(
    (quantity: number, askPricePerMg: string | number) => {
      return (quantity * Number(askPricePerMg)).toFixed(2).toString()
    },
    []
  )

  const setWeight = useCallback(
    (quantity: number, askPricePerMg: string | number) => {
      const clamped = Math.max(
        Math.min(Math.round(quantity), MAX_QUANTITY_MG),
        MIN_QUANTITY
      )
      setWeightSlider(clamped)
      setAmount(calculateAmount(clamped, askPricePerMg))
    },
    [calculateAmount]
  )

  const createReviewOrderMutation = useMutateCreateReviewOrder()
  const creatingReviewOrderForBuy = createReviewOrderMutation.isLoading

  const onSubmit = useCallback(() => {
    if (productId && !error?.error) {
      createReviewOrderMutation.mutate(
        {
          productId,
          orderType: 'buy',
          quantityMg: weightSlider
        },
        {
          onSuccess: data => {
            if (data.id) {
              navigate(
                reviewPath({
                  queryParams: { type: 'buy', productId: productId },
                  routeParams: { reviewOrderId: data.id }
                })
              )
            }
          },
          onError: (err: unknown) => {
            const error = err as { message?: string; errorStatus?: string }
            if (error?.errorStatus === TRADING_IS_PAUSED) {
              setTradingPaused(true)
            }
            setToast({
              open: true,
              message: error?.message ?? 'An error occurred',
              type: 'error',
              duration: 3000
            })
          }
        }
      )
    }
  }, [
    createReviewOrderMutation,
    error,
    navigate,
    productId,
    setToast,
    weightSlider
  ])

  const {
    data: historicalSpotPriceData,
    isLoading: historicalSpotPriceLoading
  } = useHistoricalSpotPrices(productId ?? 0, graphPeriod)

  const [, { data: latestSpotPriceData, loading: gettingLatestSpotPrice }] =
    useSpotPrice({
      callInterval: 90000, // 1.5 minutes,
      productId,
      onCompleted: latestSpotPrice => {
        const askPricePerMg = Number(latestSpotPrice?.askPricePerMg)

        // Open at a fixed default amount rather than at what the customer can
        // currently afford.
        //
        // The old behaviour capped the opening quantity by wallet balance, which
        // collapsed to the MINIMUM whenever the balance was low — and to the
        // minimum for anyone in arrears, because a NEGATIVE balance failed the
        // `walletAmount >= askPricePerMg` test and fell back to the price of a
        // single mg. Cam reported the screen opening at "BUY: $0.00" and being
        // unable to continue; his wallet was -$11.70.
        //
        // Capping by affordability was wrong in any case: checkout collects a
        // top-up, so a customer with no cash can still buy. The slider remains
        // fully editable — this only decides where it starts.
        const feeMultiplier = 1.0 + CURRENT_PURCHASE_FEE
        const quantityForDefaultAmount =
          askPricePerMg > 0
            ? Math.floor(
                Number(DEFAULT_BUY_AMOUNT) / (askPricePerMg * feeMultiplier)
              )
            : MIN_QUANTITY

        const initWeight = Math.max(
          MIN_QUANTITY,
          Math.min(quantityForDefaultAmount, MAX_QUANTITY_MG)
        )

        setWeight(initWeight, latestSpotPrice.askPricePerMg)

        setStep(stepFor(initWeight))
      }
    })

  const loading = useMemo(() => {
    return (
      creatingReviewOrderForBuy ||
      gettingLatestSpotPrice ||
      historicalSpotPriceLoading
    )
  }, [
    creatingReviewOrderForBuy,
    gettingLatestSpotPrice,
    historicalSpotPriceLoading
  ])

  const askPricePerMg = useMemo(() => {
    const { askPricePerMg } = latestSpotPriceData ?? {}
    return askPricePerMg ? Number(askPricePerMg) : 0
  }, [latestSpotPriceData])

  const updatedTime = useMemo(() => {
    const { spotTime } = latestSpotPriceData ?? {}
    return formatDateToString(new Date(spotTime ?? 0))
  }, [latestSpotPriceData])

  // Andrew's comment: In here, where we have latestSpotPriceData, we _should_ be able to replace the last element of the `builtData` with the data from latestSpotPrice.
  // because historicalSpotPriceData only includes an older spotPrice generated once per day.
  const builtData: DataPoint[] = useMemo(
    () =>
      historicalSpotPriceData && historicalSpotPriceData.length > 0
        ? buildDataHistoricalSpotPrices(historicalSpotPriceData, askPricePerMg)
        : [],
    [historicalSpotPriceData, askPricePerMg]
  )

  // Returns the quantity (in the product's unit_type) purchasable for a
  // given dollar amount. Naming preserved for diff minimisation; the
  // function no longer has anything to do with decigrams.
  const calculateDecigrams = useCallback(
    (amount: number) =>
      amount > 0 && askPricePerMg > 0
        ? Math.max(Math.round(amount / Number(askPricePerMg)), MIN_QUANTITY)
        : MIN_QUANTITY,
    [askPricePerMg]
  )

  const handleChangeWeightInput = (e: ChangeEvent<HTMLInputElement>) => {
    const weightGrams = Number(e.target.value)
    const weightMg = weightGrams * 1000

    if (weightMg === weightSlider) {
      return
    }
    setWeight(weightMg, askPricePerMg)
  }

  const handleChangeAmountInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === amount) {
      return
    }
    const weight = calculateDecigrams(Number(e.target.value))
    setWeight(weight, askPricePerMg)
  }

  const handleOnBlurAmountInput = () => {
    const actualAmount = (
      calculateDecigrams(Number(amount)) * Number(askPricePerMg)
    )
      .toFixed(2)
      .toString()

    if (amount !== actualAmount) {
      setAmount(actualAmount)
      setError({
        message: `We've rounded to $${actualAmount}`,
        error: false
      })
    }
  }

  const handleIncreaseWeight = useCallback(() => {
    if (!loading) {
      const next = incrementQuantity(weightSlider, MAX_QUANTITY_MG)
      setWeight(next, askPricePerMg)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askPricePerMg, amount, loading, weightSlider])

  const handleDecreaseWeight = useCallback(() => {
    if (!loading) {
      const next = decrementQuantity(weightSlider, MIN_QUANTITY)
      setWeight(next, askPricePerMg)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askPricePerMg, amount, loading, weightSlider])

  const goToReferralPage = useCallback(() => {
    navigate(referralsPath())
  }, [navigate])

  useEffect(() => {
    const header = document.getElementById('app-header')
    const productBullion = document.getElementById('button-wrapper')
    if (header) {
      setHeaderHeight(header.offsetHeight)
    }
    if (productBullion) {
      setProductBullionHeight(productBullion.offsetHeight)
    }

    window?.addEventListener('scroll', () => {
      const header = document.getElementById('app-header')
      const productBullion = document.getElementById('button-wrapper')
      const slideWrapper = document.getElementById('slide-wrapper')
      if (slideWrapper && header && productBullion) {
        if (
          slideWrapper.getBoundingClientRect().top >=
          Number(header.offsetHeight) + Number(productBullion.offsetHeight)
        ) {
          setHeaderHeight(header.offsetHeight)
          header.style.top = '0'
        } else {
          setHeaderHeight(0)
          header.style.top = `-${header.offsetHeight}px`
        }
      }
    })
  }, [])

  useEffect(() => {
    if (weightSlider && !Number.isInteger(weightSlider)) {
      setError({
        message: 'Enter a value with up to 3 decimal places',
        error: true
      })
    } else if (weightSlider > MAX_QUANTITY_MG) {
      setError({
        message: 'You must provide a quantity between 0.001g and 500g',
        error: true
      })
    } else {
      setError(undefined)
    }
    setStep(stepFor(weightSlider))
  }, [weightSlider, unitType])

  const sliderScalingFactor = useMemo(() => {
    if (product?.slidingScalingFactor) {
      return product.slidingScalingFactor
    }

    return 1
  }, [product])

  if (tradingPaused) {
    return <PauseTrading />
  }

  return (
    <DefaultLayout
      wrapperContent={{ mt: 0, mb: 2, px: 0 }}
      wrapperContainer={{ p: 0, position: 'relative' }}
      showKiaOra
      showFooter
      pageTitle='Goldie'
    >
      <Box
        className={classes.buyButtonWrapper}
        component='section'
        id='button-wrapper'
        top={isDesktopView ? 0 : headerHeight}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          paddingX={2}
        >
          <Box sx={{ height: '40px' }}>
            <Typography
              variant='body1'
              textTransform='uppercase'
              lineHeight={1}
            >
              {product?.name} BULLION
            </Typography>
            <Box sx={{ display: 'flex', gap: '0.5rem', lineHeight: 1 }}>
              <Typography>LIVE: </Typography>
              <CountUpPrice
                value={Number(askPricePerMg ?? 0) * 1000}
                suffix='/g'
                prefix='$'
                className={classes.livePrice}
              />
            </Box>
          </Box>

          <Box>
            {product?.tradable ? (
              <Button
                onClick={onSubmit}
                sx={{ borderRadius: '2rem', height: '38px', fontSize: '1rem' }}
              >
                Buy: ${formattedPrice(amount)}
              </Button>
            ) : (
              <Button
                disabled={true}
                sx={{ borderRadius: '2rem', height: '38px', fontSize: '1rem' }}
              >
                Coming Soon!
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Box height='100%' position='relative'>
        <Box
          position='sticky'
          top={
            isDesktopView
              ? productBullionHeight
              : productBullionHeight + headerHeight
          }
          left={0}
          right={0}
          bottom={0}
          overflow='hidden'
          // height={`calc(100vh - ${
          //   isDesktopView ? 0 : headerHeight
          // }px - ${productBullionHeight}px)`}
          height={`calc(100vh - ${isDesktopView ? 0 : headerHeight}px - ${
            productBullionHeight + (isDesktopView ? 60 : 60)
          }px)`}
          minHeight={280}
          display='flex'
          flexDirection='column'
          pt={0.25}
          sx={{
            transition: 'height 0.5s'
          }}
          gap={2}
        >
          <Box display='flex' flexDirection='column'>
            <Stack
              position='relative'
              justifyContent='center'
              alignItems='flex-end'
              //marginLeft={2}
              px={2}
              height={50}
              minHeight={50}
            >
              <Box height={1}>
                <Typography variant='body2' textAlign='right' color='error'>
                  {error?.message}
                </Typography>
              </Box>
              <InputNumber
                name='amount'
                value={amount}
                onChange={handleChangeAmountInput}
                onBlur={handleOnBlurAmountInput}
                unitType={unitType}
              />
              <InputNumber
                name='weight'
                value={weightSlider / 1000}
                onChange={handleChangeWeightInput}
                unitType={unitType}
              />
            </Stack>
          </Box>

          <Box
            display='flex'
            flexDirection='row'
            alignItems='flex-start'
            justifyContent='space-between'
            maxHeight='calc(100% - 70px - 110px)' // magical px values correspond to boxes above and below
            position='relative'
          >
            {product && (
              <img
                src={product?.image}
                alt='{product.name}-bullion'
                className={classes.productImage}
              />
            )}
            <Box
              display='flex'
              flexDirection='column'
              justifyContent='space-between'
              alignItems='center'
              position='absolute'
              top='0'
              right='0'
              bottom='0'
              mr={2}
              gap={2}
            >
              <Box onClick={handleIncreaseWeight} textAlign='center'>
                <KeyboardArrowUpIcon fontSize='large' color='primary' />
              </Box>
              <SliderMui
                aria-label='weight'
                id='weight'
                size='small'
                orientation='vertical'
                valueLabelDisplay='off'
                defaultValue={0}
                step={step}
                min={MIN_QUANTITY}
                max={Math.min(50_000 * sliderScalingFactor, MAX_QUANTITY_MG)}
                sx={{ height: '100%' }}
                name='weight'
                value={weightSlider}
                onChange={(_event, value) => {
                  setWeight(Number(value) || MIN_QUANTITY, askPricePerMg)
                }}
                className={classes.slider}
                disabled={loading}
              />
              <Box onClick={handleDecreaseWeight} textAlign='center'>
                <KeyboardArrowDownIcon fontSize='large' color='primary' />
              </Box>
            </Box>
          </Box>
          <Box
            display='flex'
            minHeight='95px'
            justifyContent='center'
            gap={1}
            alignItems='center'
            px={2}
            overflow='auto'
            mb={1}
          >
            {products &&
              products.map(p => {
                return (
                  <Box
                    key={p.id}
                    display='flex'
                    flexDirection='column'
                    justifyContent='center'
                    alignItems='center'
                    gap={1}
                    onClick={() => {
                      navigate(tradePath({ routeParams: { productId: p.id } }))
                    }}
                  >
                    <img
                      src={p?.image}
                      alt='{product.name}-bullion'
                      className={classes.productSwitcherImage}
                    />
                    <Box
                      className={
                        Number(p?.id) === productId
                          ? classes.activeDot
                          : classes.dot
                      }
                    />
                  </Box>
                )
              })}
          </Box>
          <Box display='flex' minHeight='0' flex='1'>
            {/*filler box -- fills up remaining free space*/}
          </Box>
        </Box>
        <Box className={classes.stickyLine} mt={1}></Box>
        <Box
          sx={{
            background: 'transparent',
            backdropFilter: 'blur(20px)'
            // borderTop: ({ palette }) => `1px solid ${palette.black.main}`
          }}
          pt={1}
          id='slide-wrapper'
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            px={2}
          >
            <Box>
              <Button
                variant='outlined'
                sx={{
                  borderRadius: '2rem',
                  padding: '0 0.5rem',
                  height: '38px'
                }}
                onClick={goToReferralPage}
              >
                <Typography variant='body1' color='primary'>
                  SHARE $20
                </Typography>
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Typography>CREDIT: </Typography>
              <CountUpPrice value={Number(user?.wallet?.balance)} prefix='$' />
            </Box>
          </Box>
          <Divider className={classes.divider} />
          <Stack height={300} mb={10} position='relative'>
            <Typography
              className={classes.aboutDesc}
              paddingLeft={2}
              textTransform='uppercase'
            >
              {product?.name} PERFORMANCE
            </Typography>
            <LoadingWrapper loading={historicalSpotPriceLoading}>
              <LineGraph
                data={builtData}
                graphPeriod={graphPeriod}
                beginAtZero={false}
                colors={{
                  line: theme.palette.black.main,
                  tooltipCircle: theme.palette.primary.main,
                  axis: theme.palette.black.main
                }}
                tickFormatter={(value: NumberValue) =>
                  `$${Number(value).toFixed(2)}`
                }
                showChangeValue={false}
                showXAxis
                loading={historicalSpotPriceLoading}
                showBackgroundColor={false}
                isCustomTooltip={false}
              />
            </LoadingWrapper>
          </Stack>
          <Stack>
            <GraphPeriod
              setGraphPeriod={setGraphPeriod}
              graphPeriod={graphPeriod}
            />
          </Stack>
          <Stack>
            <Typography
              textAlign='right'
              fontSize='0.75rem'
              textTransform='uppercase'
              px={1}
            >
              UPDATED {updatedTime.formattedHours}:
              {updatedTime.formattedMinutes} {updatedTime.meridiem}{' '}
              {updatedTime.formattedDate}
            </Typography>
          </Stack>
          <Stack
            px={2}
            my={2}
            borderTop={({ palette }) => `1px solid ${palette.black.main}`}
          >
            <Typography className={classes.aboutDesc} color='primary'>
              ABOUT
            </Typography>
            {product?.descriptions.map((description, index) => (
              <Typography
                className={classes.aboutDescSecond}
                key={index}
                fontSize={index === 0 ? 'x-large' : undefined}
              >
                {description}
              </Typography>
            ))}
          </Stack>

          <Stack
            px={2}
            borderTop={({ palette }) => `1px solid ${palette.black.main}`}
          >
            <Typography className={classes.aboutDesc}>LIVE LOCATION</Typography>
            <Location />
          </Stack>
        </Box>
      </Box>
    </DefaultLayout>
  )
}
